import { Decimal } from 'decimal.js';
import type { VatSummaryItem as GqlVatSummaryItem } from '../../../../graphql/generated/graphql';
import type { VatSummaryItem } from '../../../../modules/modals/EditVatSummaryModal';

export interface VatSummaryWithTotals {
  totalAmountNoVat: Decimal;
  totalAmount: Decimal;
  vatSummary: VatSummaryItem[];
}

const isDecimalOrNullish = (v: unknown): boolean => v == null || Decimal.isDecimal(v);

export const isVatSummaryItem = (value: unknown): value is GqlVatSummaryItem =>
  typeof value === 'object' &&
  value !== null &&
  isDecimalOrNullish((value as GqlVatSummaryItem).vatRate) &&
  isDecimalOrNullish((value as GqlVatSummaryItem).netAmount) &&
  isDecimalOrNullish((value as GqlVatSummaryItem).vatAmount);

export const isVatSummary = (value: unknown): value is VatSummaryWithTotals =>
  typeof value === 'object' &&
  value !== null &&
  'totalAmountNoVat' in value &&
  'totalAmount' in value &&
  Decimal.isDecimal((value as VatSummaryWithTotals).totalAmountNoVat) &&
  Decimal.isDecimal((value as VatSummaryWithTotals).totalAmount);

/**
 * Normalize either shape the FieldEditor pipeline may hand to the
 * vat-summary editor/formatter into a plain item array. The host typically
 * passes `VatSummaryItem[]`, but EditableField re-renders the editor with
 * the full `VatSummaryFormData` object while a save is in flight (the
 * intermediate `setValue(newVal)` step in `handleCommit`). Returning `[]`
 * for unrecognized shapes avoids crashes on transient bad input.
 */
export const toVatSummaryItems = (
  value: unknown
): ReadonlyArray<GqlVatSummaryItem | VatSummaryItem> => {
  if (Array.isArray(value)) return value as Array<GqlVatSummaryItem | VatSummaryItem>;
  if (isVatSummary(value)) return value.vatSummary;
  return [];
};

/**
 * Compute totals over a list of VAT summary items. Items with null/undefined
 * Decimal fields (possible when displaying partially-extracted data) are
 * skipped — non-null fields contribute to the totals.
 */
export const computeVatSummaryTotals = (
  items: ReadonlyArray<GqlVatSummaryItem | VatSummaryItem>
): VatSummaryWithTotals => {
  const validItems: VatSummaryItem[] = items
    .filter(
      (i): i is VatSummaryItem => i.vatRate != null && i.netAmount != null && i.vatAmount != null
    )
    .map(i => ({ vatRate: i.vatRate!, netAmount: i.netAmount!, vatAmount: i.vatAmount! }));

  if (validItems.length === 0) {
    return {
      totalAmountNoVat: new Decimal(0),
      totalAmount: new Decimal(0),
      vatSummary: []
    };
  }
  const totalAmountNoVat = validItems.reduce((s, i) => s.plus(i.netAmount), new Decimal(0));
  const vatTotal = validItems.reduce((s, i) => s.plus(i.vatAmount), new Decimal(0));
  return {
    totalAmountNoVat,
    totalAmount: totalAmountNoVat.plus(vatTotal),
    vatSummary: validItems
  };
};
